/**
 * @file Web utility functions.
 * @author andreas.z.hellstrom@teliacompany.com (Andreas Hellström)
 */

import { isString } from './typeUtils';

//------------------------------------------------------------------------------
// Exported functions
//------------------------------------------------------------------------------

/**
 * Get URL param from URL. If more than one param with same name exists, the first one is returned.
 * @param {string} name URL param name to get.
 * @param {string} url URL to use. If omitted, window location href is used.
 * @param {Object} valueForNotFound Return value if param is not found.
 * @param {bool} decodeParams Should param value be URL decoded or not?
 * @returns {*} URL param value, or undefined.
 */
export function getUrlParam(
  name,
  url = window.location.href,
  valueForNotFound = undefined,
  decodeParams = true
) {
  name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]'); // eslint-disable-line
  const regexS = '[\\?&]' + name + '=([^&#]*)'; // eslint-disable-line
  const regex = new RegExp(regexS);

  const results = regex.exec(url);
  if (results == null) {
    return valueForNotFound;
  }

  const result = results[1];
  return decodeParams ? decodeURIComponent(result) : result;
}

/**
 * getUrlVars
 *
 * Read a page's GET URL variables and return them as an associative array.
 */
export function getUrlVars() {
  const vars = [];
  let hash;
  const hashes = window.location.href.slice(window.location.href.indexOf('?') + 1).split('&');
  for (let i = 0; i < hashes.length; i++) {
    hash = hashes[i].split('=');
    vars.push(hash[0]);
    vars[hash[0]] = hash[1];
  }
  return vars;
}

/**
 * Checks if a string is a well-formed URI.
 * Source: https://gist.github.com/dperini/729294
 * @param {string} str - The string to check
 * @returns {boolean}
 */
export const isUri = (str) => {
  if (!isString(str)) return false;
  const reWeburl = new RegExp(
    // eslint-disable-next-line max-len
    /^(?:(?:https?|ftp):\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,}))\.?)(?::\d{2,5})?(?:[/?#]\S*)?$/i
  );
  return reWeburl.test(str);
};

/**
 * Parses an URI and split a well-formed URI into its parts.
 * @param {string} uri - The URI
 * @returns {Object}
 */
export const parseUri = (uri) => {
  parseUrl.options.strictMode = true;
  const urlParts = parseUrl(uri);
  urlParts.baseUrl = `${urlParts.protocol}://${urlParts.host}`;
  urlParts.baseUrl += urlParts.port ? `:${urlParts.port}` : '';
  const dir = urlParts.directory;
  urlParts.directory += dir.slice(dir.length - 1) === '/' ? '' : '/';

  return urlParts;
};

export function isValidUrl(href) {
  try {
    // eslint-disable-next-line no-unused-vars
    const url = new URL(href);
    return true;
  } catch (error) {
    return false;
  }
}

//------------------------------------------------------------------------------
// External functions
//------------------------------------------------------------------------------

/**
 * parseUri 1.2.2
 * (c) Steven Levithan <stevenlevithan.com>
 * MIT License
 * Source: http://blog.stevenlevithan.com/archives/parseuri
 * Source: https://gist.github.com/ir-g/4642307
 */

function parseUrl(str) {
  const o = parseUrl.options;
  const m = o.parser[o.strictMode ? 'strict' : 'loose'].exec(str);
  const uri = {};
  let i = 14;

  while (i--) uri[o.key[i]] = m[i] || '';

  uri[o.q.name] = {};
  uri[o.key[12]].replace(o.q.parser, ($0, $1, $2) => {
    if ($1) uri[o.q.name][$1] = $2;
  });

  return uri;
}

parseUrl.options = {
  strictMode: false,
  key: [
    'source',
    'protocol',
    'authority',
    'userInfo',
    'user',
    'password',
    'host',
    'port',
    'relative',
    'path',
    'directory',
    'file',
    'query',
    'anchor',
  ],
  q: {
    name: 'queryKey',
    parser: /(?:^|&)([^&=]*)=?([^&]*)/g,
  },
  parser: {
    strict:
      /^(?:([^:\/?#]+):)?(?:\/\/((?:(([^:@]*)(?::([^:@]*))?)?@)?([^:\/?#]*)(?::(\d*))?))?((((?:[^?#\/]*\/)*)([^?#]*))(?:\?([^#]*))?(?:#(.*))?)/, // eslint-disable-line max-len, no-useless-escape
    loose:
      /^(?:(?![^:@]+:[^:@\/]*@)([^:\/?#.]+):)?(?:\/\/)?((?:(([^:@]*)(?::([^:@]*))?)?@)?([^:\/?#]*)(?::(\d*))?)(((\/(?:[^?#](?![^?#\/]*\.[^?#\/.]+(?:[?#]|$)))*\/?)?([^?#\/]*))(?:\?([^#]*))?(?:#(.*))?)/, // eslint-disable-line max-len, no-useless-escape
  },
};
